import { csx } from '@vtex/admin-ui'
import React, { FC, useState, useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'

import Divider from '@vtex/styleguide/lib/Divider'
import ProductAvailability from '@/checkout-instore-legacy/components/Commons/ProductAvailabilityInfo'
import ProductSummary, {
  ItemProductCart,
  BorderType,
} from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductSummary'
import { ServiceInterface } from '@/checkout-instore-legacy/components/Commons/ProductSummary/ServiceOptions'
import { goToProduct } from '@/checkout-instore-legacy/utils/ProductUtils'
import {
  logChangeSla,
  logClick,
  logRemove,
  logChangeQuantity,
} from './logHelper'
import Caret from '@/checkout-instore-legacy/components/Commons/Icons/Caret'
import {
  ItemPayload,
  CartItemMessage,
  ScenarioType,
} from '@/checkout-instore-legacy/cart/message/types'
import { getMessageAggregate } from '@/checkout-instore-legacy/cart/message/utils'
import ProductItemHelperText from '@/checkout-instore-legacy/components/Commons/ProductSummary/Message/ProductItemHelperText'
import { toCartDisplayMessage } from '@/checkout-instore-legacy/cart/message/factories'
import {
  getBlockProductProperties,
  blockProductInitialState,
} from '@/checkout-instore-legacy/cart/components/ProductList/utils'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'

export interface ProductItemActions {
  onChangeSla: (item: Item) => void
  switchSeller: (item: Item) => void
  changeItemQuantity: (item: ItemPayload) => void
  removeItem: (item: ItemPayload) => void
  changeOffering: (
    selectedServices: Array<string>,
    offeringId: number,
    itemPayload: ItemPayload
  ) => void
  updateStock: (skuId: string) => void
}

export type ProductItemProps = {
  item: ItemProductCart & { offerings: Array<ServiceInterface> }
  cartMessages: CartItemMessage[]
  loading: boolean
  router: RouterProps<ItemProductCart, any>
  availability: Record<string, boolean | string | number>
  isLastInList?: boolean
} & ProductItemActions

const mapToBorderType = (
  scenarioType: Optional<ScenarioType>
): BorderType | undefined => {
  switch (scenarioType) {
    case ScenarioType.PENDENCY:
      return BorderType.danger
    case ScenarioType.UNEXPECTED:
      return BorderType.warning
    default:
      return
  }
}

const ProductItem: FC<ProductItemProps> = ({
  item,
  cartMessages,
  loading,
  changeItemQuantity,
  removeItem,
  changeOffering,
  onChangeSla,
  switchSeller,
  router,
  availability,
  updateStock,
}) => {
  const [openAvailability, setOpenAvailability] = useState(false)
  const blockProperties = getBlockProductProperties(cartMessages)

  const { numeric, footer, options } =
    blockProperties || blockProductInitialState
  const iconClass = footer.show
    ? 'c-action-primary hover-c-action-primary'
    : 'c-muted-2'
  const style = {
    transform: `rotate(${openAvailability ? '180' : '0'}deg)`,
  }

  const { value: isMobile } = useMediaQuery({
    phone: true,
    tablet: false,
    desktop: false,
  })

  const icon = <Caret className={iconClass} style={style} />

  const handleClick = footer.show ? setOpenAvailability : () => {}

  const messageAggregate = getMessageAggregate(
    cartMessages.map(toCartDisplayMessage)
  )

  const handleGoToProduct = () => {
    goToProduct(router, item)

    logClick(item)
  }

  const handleOnChangeSla = (item: Item) => {
    onChangeSla(item)
    logChangeSla(item)
  }

  const handleSwitchSeller = (item: Item) => {
    switchSeller(item)
  }

  const handleRemoveItem = (itemPayload: ItemPayload) => {
    removeItem(itemPayload)
    logRemove(itemPayload)
  }

  const handleChangeItemQuantity = (itemPayload: ItemPayload) => {
    changeItemQuantity(itemPayload)
    logChangeQuantity(itemPayload)
  }

  const handleChangeOffering = (
    selectedServices: Array<string>,
    offeringId: number,
    itemPayload: ItemPayload
  ) => {
    changeOffering(selectedServices, offeringId, itemPayload)
  }

  useEffect(
    function showProductFooterOnlyWithoutErrors() {
      const containsError = !isEmpty(cartMessages)
      if (containsError) {
        setOpenAvailability(false)
      }
    },
    [cartMessages]
  )

  const renderHelpText = () => {
    if (!messageAggregate.helperText) {
      return null
    }
    return (
      <div className="ph5 ph0-ns">
        <ProductItemHelperText text={messageAggregate.helperText} />
      </div>
    )
  }

  const getBundleItems = () => {
    if (item.bundleItems && item.bundleItems.length > 0) {
      return item.bundleItems.map((bundleItem) => bundleItem.id)
    }
    return []
  }

  return (
    <div className={`${isEmpty(messageAggregate.helperText) ? '' : 'pb5'}`}>
      <ProductSummary
        item={item}
        onClick={handleGoToProduct}
        borderType={mapToBorderType(messageAggregate.scenario)}
      >
        <ProductSummary.Name />
        <ProductSummary.Options
          removeItem={handleRemoveItem}
          changeSla={handleOnChangeSla}
          switchSeller={handleSwitchSeller}
          enabled={options}
        />
        {!isMobile && (
          <ProductSummary.ServiceOptions
            services={item.offerings}
            changeOffering={handleChangeOffering}
            bundleItems={getBundleItems()}
          />
        )}
        <ProductSummary.Content>
          <ProductSummary.Price
            loading={loading}
            availabilityText={messageAggregate.priceAvailabilityText}
          />
          {isMobile && (
            <div
              className={csx({
                marginTop: '$space-3',
              })}
            >
              <ProductSummary.ServiceOptions
                services={item.offerings}
                changeOffering={handleChangeOffering}
                bundleItems={getBundleItems()}
              />
            </div>
          )}
          <div className="dn-ns mt4 mb2 mb3-s bg-lights-silver">
            <Divider orientation="horizontal" />
          </div>
          <ProductSummary.ClickableArea>
            <div className="flex justify-between-s items-end-ns">
              <ProductSummary.NumericStepper
                numericStepperProps={{
                  readOnly: !numeric.enable,
                  minValue: numeric.minValue,
                  maxValue: numeric.maxValue,
                }}
                changeItemQuantity={handleChangeItemQuantity}
              />
              <div className="mr6" />
              <div
                data-testid="product-item__collapsible-button"
                className={`h2 w2 pv1 flex items-center justify-center `}
                onClick={() => {
                  handleClick((open) => !open)
                }}
              >
                {icon}
              </div>
            </div>
          </ProductSummary.ClickableArea>
        </ProductSummary.Content>
        <ProductSummary.Footer show={openAvailability} type="collapsable">
          <ProductSummary.ClickableArea>
            <div className="flex flex-row w-100 h-100 mb3-s mb0-ns">
              <div className="bg-transparent product-summary__image dn-nsl mr5-ns mr4-s" />
              <div className="ba overflow-auto-s overflow-hidden-ns b--muted-4 br2 pa3 mt5-ns mt3-s w-100 h-100">
                <ProductAvailability
                  // @ts-expect-error ts-migrate(2322) FIXME: Type 'void' is not assignable to type '(skuId: str... Remove this comment to see the full error message
                  handleRefresh={() => updateStock(item.id)}
                  type="row"
                  {...availability}
                />
              </div>
            </div>
          </ProductSummary.ClickableArea>
        </ProductSummary.Footer>
      </ProductSummary>
      {renderHelpText()}
    </div>
  )
}

export default ProductItem
